import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Verify1PCoverageModal } from '@/components/tasks/verify-1p-coverage/Verify1PCoverageModal';
import { cn } from '@/lib/utils';
import { useGetTasksQuery } from '@/services/api';
import {
  useMergeCoveragesMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import type { Task } from '@/services/types/client-intake-types';
import {
  CoverageCategory,
  type CoverageListItem,
  CoverageSource,
  CoverageStatus,
} from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { CoverageAlertBanner } from './shared/CoverageAlertBanner';
import { CoverageCardRedesign } from './shared/CoverageCardRedesign';
import { useCoverageListLogic } from './shared/hooks';
import { useCoverage1PVerificationPolicy } from './shared/useCoverage1PVerificationPolicy';
import { getCachePriority } from './shared/utils/cacheUtils';
import {
  findVerify1PTask,
  is1PVerificationNeeded,
} from './shared/utils/verificationUtils';

/** Statuses that should be hidden from the main coverage list sections */
const isHiddenStatus = (status: CoverageStatus | null) =>
  status === CoverageStatus.REJECTED || status === CoverageStatus.DUPLICATE;

interface CoverageListProps {
  coverages: CoverageListItem[];
  onCoverageSelect: (coverageId: string) => void;
  filterCategory?: CoverageCategory;
  matterId: string;
}

export const CoverageList = ({
  coverages,
  onCoverageSelect,
  filterCategory,
  matterId,
}: CoverageListProps) => {
  const [updateCoverage] = useUpdateCoverageMutation();
  const [mergeCoverages] = useMergeCoveragesMutation();

  // Fetch tasks for section-level empty-state indicators
  const { data: tasksResponse, refetch: refetchTasks } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });
  const matterTasks: Task[] = ((tasksResponse?.results as Task[]) ||
    []) as Task[];
  const find1pTasks = matterTasks.filter(
    (t) => t.type?.canonical_type === CanonicalTaskType.FIND_1P_COVERAGE,
  );
  const find3pTasks = matterTasks.filter(
    (t) => t.type?.canonical_type === CanonicalTaskType.FIND_3P_COVERAGE,
  );
  const hasNoCoverageApplicable1p =
    find1pTasks.some(
      (t) =>
        (t.status?.name || '').toLowerCase() ===
          'no coverage found'.toLowerCase() ||
        t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
    ) || false;
  const hasNoCoverageApplicable3p =
    find3pTasks.some(
      (t) =>
        (t.status?.name || '').toLowerCase() ===
          'no coverage found'.toLowerCase() ||
        t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
    ) || false;

  // Find the Verify 1P Coverage task (if it exists)
  const verify1PTask = findVerify1PTask(matterTasks);
  const [isVerify1POpen, setIsVerify1POpen] = useState(false);

  const { verifyWithClientEnabled } = useCoverage1PVerificationPolicy({
    matterId,
  });

  // Use shared hooks for filtering and grouping logic
  const { filteredGroups } = useCoverageListLogic(coverages, filterCategory);

  const [showRemovedCoverages, setShowRemovedCoverages] = useState(false);

  // Separate 1P, 3P, and Health coverage with conditional display rules:
  // - If there is an approved coverage in a category, show only approved items in that section
  // - Otherwise, show unapproved items for that category in that section
  const groupFirstParty = filteredGroups[CoverageCategory.FIRST_PARTY] || [];
  const groupThirdParty = filteredGroups[CoverageCategory.THIRD_PARTY] || [];
  const groupHealth = filteredGroups[CoverageCategory.FIRST_PARTY_HEALTH] || [];
  const groupNotLiable =
    filteredGroups[CoverageCategory.NOT_LIABLE_INVOLVED_PARTY] || [];
  // Premises Liability category (displayed under 3P section)
  const groupProperty = filteredGroups[CoverageCategory.PROPERTY] || [];

  const firstPartyApproved = groupFirstParty.filter(
    (c) => c.status === 'APPROVED',
  );
  const thirdPartyApproved = groupThirdParty.filter(
    (c) => c.status === 'APPROVED',
  );
  const healthApproved = groupHealth.filter((c) => c.status === 'APPROVED');
  const notLiableApproved = groupNotLiable.filter(
    (c) => c.status === 'APPROVED',
  );
  const propertyApproved = groupProperty.filter((c) => c.status === 'APPROVED');

  // Exclude REJECTED/DUPLICATE coverages from unapproved lists - they go in the collapsible section
  const firstPartyUnapproved = groupFirstParty.filter(
    (c) => c.status !== 'APPROVED' && !isHiddenStatus(c.status),
  );
  const thirdPartyUnapproved = groupThirdParty.filter(
    (c) => c.status !== 'APPROVED' && !isHiddenStatus(c.status),
  );
  const healthUnapproved = groupHealth.filter(
    (c) => c.status !== 'APPROVED' && !isHiddenStatus(c.status),
  );
  const notLiableUnapproved = groupNotLiable.filter(
    (c) => c.status !== 'APPROVED' && !isHiddenStatus(c.status),
  );
  const propertyUnapproved = groupProperty.filter(
    (c) => c.status !== 'APPROVED' && !isHiddenStatus(c.status),
  );

  const firstPartyCoverage =
    firstPartyApproved.length > 0 ? firstPartyApproved : firstPartyUnapproved;

  const needs1PVerification = useMemo(
    () => is1PVerificationNeeded(groupFirstParty, verifyWithClientEnabled),
    [groupFirstParty, verifyWithClientEnabled],
  );

  const thirdPartyCoverage =
    thirdPartyApproved.length > 0 ? thirdPartyApproved : thirdPartyUnapproved;
  const healthInsuranceCoverage =
    healthApproved.length > 0 ? healthApproved : healthUnapproved;
  const notLiableCoverage =
    notLiableApproved.length > 0 ? notLiableApproved : notLiableUnapproved;
  const propertyCoverage =
    propertyApproved.length > 0 ? propertyApproved : propertyUnapproved;

  // Get all coverage IDs that are already assigned to 1P, 3P, Health, or Property sections
  const assignedCoverageIds = new Set([
    ...firstPartyCoverage.map((c) => c.id),
    ...thirdPartyCoverage.map((c) => c.id),
    ...healthInsuranceCoverage.map((c) => c.id),
    ...notLiableCoverage.map((c) => c.id),
    ...propertyCoverage.map((c) => c.id),
  ]);

  // Other coverage - exclude anything assigned to category sections and exclude primary categories (1P/3P/Health/Property)
  const isPrimaryCategory = (c: CoverageListItem) =>
    c.category === CoverageCategory.FIRST_PARTY ||
    c.category === CoverageCategory.THIRD_PARTY ||
    c.category === CoverageCategory.FIRST_PARTY_HEALTH ||
    c.category === CoverageCategory.NOT_LIABLE_INVOLVED_PARTY ||
    c.category === CoverageCategory.PROPERTY;

  const otherCoverage = coverages
    .filter((coverage) => !assignedCoverageIds.has(coverage.id))
    .filter((coverage) => !isPrimaryCategory(coverage))
    .filter((coverage) => !isHiddenStatus(coverage.status))
    .filter((coverage) => {
      if (filterCategory) {
        return coverage.category === filterCategory;
      }
      return true;
    });

  // Status-based groups (for bottom sections)
  const unapprovedCoverage = coverages.filter(
    (c) => c.status === 'NEEDS_APPROVAL' && !assignedCoverageIds.has(c.id),
  );
  const rejectedCoverage = coverages.filter((c) => isHiddenStatus(c.status));
  // N/A coverages - shown in collapsible section when there are approved coverages in the same category
  const notApplicableCoverage = coverages.filter(
    (c) => c.status === CoverageStatus.NOT_APPLICABLE,
  );
  const [showNACoverages, setShowNACoverages] = useState(false);

  // Helper function to sort coverage by cache priority
  const sortCoverageByPriority = (coverageList: CoverageListItem[]) => {
    return [...coverageList].sort((a, b) => {
      const priorityA = getCachePriority(a);
      const priorityB = getCachePriority(b);

      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }

      const nameA = a.carrier?.name || '';
      const nameB = b.carrier?.name || '';
      return nameA.localeCompare(nameB);
    });
  };

  // Helper function to render a stacked section
  const renderCoverageSection = (
    title: string,
    subtitle: string,
    coverageList: CoverageListItem[],
    {
      showNoApplicable,
      hideHeader,
      banner,
    }: { showNoApplicable?: boolean; hideHeader?: boolean; banner?: React.ReactNode } = {},
  ) => {
    const sortedCoverage = sortCoverageByPriority(coverageList);

    return (
      <div className="space-y-3">
        {!hideHeader && (
          <div>
            <h3 className="text-base font-semibold text-zinc-900">{title}</h3>
            <p className="text-xs font-medium text-zinc-500">{subtitle}</p>
          </div>
        )}
        {banner}

        {/* Coverage Cards - stacked list */}
        {sortedCoverage.length > 0 ? (
          <div className="space-y-3">
            {sortedCoverage.map((coverage) => (
              <CoverageCardRedesign
                key={coverage.id}
                coverage={coverage}
                matterId={matterId}
                needs1PVerification={needs1PVerification}
                readOnly={coverage.source === CoverageSource.SHARED}
                onClick={() => onCoverageSelect(coverage.id)}
                onDelete={coverage.source === CoverageSource.SHARED ? undefined : async (id) => {
                  try {
                    await updateCoverage({
                      matterId,
                      coverageId: id,
                      coverage: { status: CoverageStatus.REJECTED },
                    }).unwrap();
                    toast.success('Coverage rejected');
                  } catch {
                    toast.error('Failed to reject coverage');
                  }
                }}
                onMerge={coverage.source === CoverageSource.SHARED ? undefined : async (dstId, srcId) => {
                  try {
                    await mergeCoverages({
                      matterId,
                      mergeCoverageRequest: {
                        dst_coverage_id: dstId,
                        src_coverage_ids: [srcId],
                      },
                    }).unwrap();
                    toast.success('Coverages merged');
                  } catch {
                    toast.error('Merge failed');
                  }
                }}
                allCoverages={coverages}
              />
            ))}
          </div>
        ) : showNoApplicable ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">
              No coverage applicable
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 py-4 text-center">
            <p className="text-sm text-gray-500">No approved coverages yet</p>
          </div>
        )}
      </div>
    );
  };

  const combinedOtherCoverage = (() => {
    const otherCoverageIds = new Set(
      otherCoverage.map((coverage) => coverage.id),
    );
    const merged = [...otherCoverage];

    unapprovedCoverage.forEach((coverage) => {
      if (!otherCoverageIds.has(coverage.id)) {
        merged.push(coverage);
      }
    });

    return merged;
  })();

  const shouldRenderNotLiable =
    filterCategory === CoverageCategory.NOT_LIABLE_INVOLVED_PARTY ||
    notLiableCoverage.length > 0;

  // Premises Liability section - displayed under 3P section
  const shouldRenderProperty =
    filterCategory === CoverageCategory.PROPERTY || propertyCoverage.length > 0;

  return (
    <div className="space-y-8">
      {shouldRenderProperty &&
        renderCoverageSection(
          'Properties',
          'Premises liability properties',
          propertyCoverage,
        )}

      {renderCoverageSection(
        '1st Party Coverage',
        "Client's coverage",
        firstPartyCoverage,
        {
          showNoApplicable: hasNoCoverageApplicable1p,
          banner:
            needs1PVerification ? (
              <CoverageAlertBanner
                message="Client verification needed to open 1P coverages."
                actionLabel={verify1PTask ? 'Verify Coverage' : 'Refresh tasks'}
                onAction={() => {
                  if (verify1PTask) {
                    setIsVerify1POpen(true);
                    return;
                  }
                  refetchTasks();
                }}
              />
            ) : undefined,
        },
      )}

      {renderCoverageSection(
        '3rd Party Coverage',
        "At-fault party's coverage",
        thirdPartyCoverage,
        { showNoApplicable: hasNoCoverageApplicable3p },
      )}

      {shouldRenderNotLiable &&
        renderCoverageSection(
          'Not-Liable Involved Parties',
          'Coverage for involved parties currently marked not at fault',
          notLiableCoverage,
        )}

      {renderCoverageSection(
        'Health Insurance',
        "Client's health insurance",
        healthInsuranceCoverage,
      )}

      {combinedOtherCoverage.length > 0 &&
        renderCoverageSection(
          'Other & Unapproved Coverage',
          'Unclear, uncategorized, or awaiting approval',
          combinedOtherCoverage,
        )}

      {notApplicableCoverage.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white">
          <button
            type="button"
            className="flex w-full items-center justify-between px-4 py-3"
            onClick={() => setShowNACoverages(!showNACoverages)}
            aria-expanded={showNACoverages}
            aria-controls="na-coverages-panel"
          >
            <div className="flex items-center gap-3">
              <span className="text-base font-semibold text-gray-900">
                Not Applicable
              </span>
              <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
                {notApplicableCoverage.length}
              </span>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-gray-500 transition-transform',
                showNACoverages && 'rotate-180',
              )}
            />
          </button>

          {showNACoverages && (
            <div
              id="na-coverages-panel"
              className="border-t border-gray-100 px-4 py-6"
            >
              <p className="text-sm text-gray-500">
                Coverage marked as not applicable for this case
              </p>
              <div className="mt-4">
                {renderCoverageSection(
                  'Not Applicable',
                  'Coverage marked as not applicable',
                  notApplicableCoverage,
                  { hideHeader: true },
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {rejectedCoverage.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white">
          <button
            type="button"
            className="flex w-full items-center justify-between px-4 py-3"
            onClick={() => setShowRemovedCoverages(!showRemovedCoverages)}
            aria-expanded={showRemovedCoverages}
            aria-controls="removed-coverages-panel"
          >
            <div className="flex items-center gap-3">
              <span className="text-base font-semibold text-gray-900">
                Removed Coverages
              </span>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {rejectedCoverage.length}
              </span>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-gray-500 transition-transform',
                showRemovedCoverages && 'rotate-180',
              )}
            />
          </button>

          {showRemovedCoverages && (
            <div
              id="removed-coverages-panel"
              className="border-t border-gray-100 px-4 py-6"
            >
              <p className="text-sm text-gray-500">
                Coverage that has been removed
              </p>
              <div className="mt-4">
                {renderCoverageSection(
                  'Removed Coverages',
                  'Coverage that has been removed',
                  rejectedCoverage,
                  { hideHeader: true },
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Verify 1P Coverage Modal */}
      {isVerify1POpen && verify1PTask && (
        <Verify1PCoverageModal
          isOpen={isVerify1POpen}
          onClose={() => setIsVerify1POpen(false)}
          matterId={matterId}
          taskId={verify1PTask.id}
        />
      )}
    </div>
  );
};
